import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Input, Button, Typography, theme } from 'antd';
import { AnimatePresence, motion, useDragControls } from 'framer-motion';
import { IconCircleCheck, IconAlertTriangle, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { message } from '../services/toastMessage';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';
import SignaturePad, { SignaturePadHandle } from './SignaturePad';
import { acknowledgeCheckInOutReport } from '../services/CheckInOutService';
import {
  AcknowledgementOutcome,
  CheckInOutReportType,
  ICheckInOutReport,
} from '../interfaces/heavyDutyMachinery/ICheckInOut';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { TEAL, TEAL_SOFT_BG } from '../theme/creamyTokens';

const { TextArea } = Input;
const { Text } = Typography;

interface OutcomeChoice {
  outcome: AcknowledgementOutcome;
  icon: React.ElementType;
  labelKey: string;
  detailKey: string;
}

// Order matters: the one a delivery normally ends in comes first, and the
// destructive one last, so the common case is the shortest reach on a phone.
const OUTCOMES: OutcomeChoice[] = [
  {
    outcome: 'Accepted',
    icon: IconCircleCheck,
    labelKey: TRANSLATIONS.HANDOVER_ACK_ACCEPTED,
    detailKey: TRANSLATIONS.HANDOVER_ACK_ACCEPTED_DETAIL,
  },
  {
    outcome: 'AcceptedWithRemarks',
    icon: IconAlertTriangle,
    labelKey: TRANSLATIONS.HANDOVER_ACK_ACCEPTED_WITH_REMARKS,
    detailKey: TRANSLATIONS.HANDOVER_ACK_ACCEPTED_WITH_REMARKS_DETAIL,
  },
  {
    outcome: 'Rejected',
    icon: IconX,
    labelKey: TRANSLATIONS.HANDOVER_ACK_REJECTED,
    detailKey: TRANSLATIONS.HANDOVER_ACK_REJECTED_DETAIL,
  },
];

interface HandoverAcknowledgementSheetProps {
  bookingId: string;
  reportType: CheckInOutReportType;
  open: boolean;
  onClose: () => void;
  onAcknowledged: (report: ICheckInOutReport) => void;
}

/**
 * The counterparty's half of the albarán: the screen where the person who
 * did NOT write the delivery note says whether they agree with it.
 *
 * Three outcomes, not two, and the choice is the whole screen rather than a
 * yes/no pair of buttons, because which one you pick has genuinely
 * different consequences and the middle one is the one people actually
 * need. "Conforme con reservas" is how a delivery ends when the machine is
 * fine to work with but something is off: the crew gets on with the job AND
 * the scratch that was already there is on the record, which is precisely
 * what stops it becoming somebody's word against nothing at the end of the
 * rental.
 *
 * Rejecting is deliberately not styled as a scary red trapdoor. It is a
 * legitimate outcome -- the machine that turned up is not the one that was
 * paid for -- and because the platform is still holding the money (see the
 * backend's Booking.FundsReleasedToHostAt), nothing irreversible happens
 * when someone picks it: the release simply never fires, and a human sorts
 * it out. A guest who is unsure should still be able to say no.
 *
 * Same desktop-Modal / mobile-bottom-sheet split as HostCancelBookingModal
 * -- see that component's own doc comment.
 */
const HandoverAcknowledgementSheet: React.FC<HandoverAcknowledgementSheetProps> = ({
  bookingId,
  reportType,
  open,
  onClose,
  onAcknowledged,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { token } = theme.useToken();
  const signatureRef = useRef<SignaturePadHandle>(null);
  const [outcome, setOutcome] = useState<AcknowledgementOutcome>('Accepted');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Same reasoning as HostCancelBookingModal's own: this sheet holds a
  // TextArea and a canvas the finger needs to draw on, so a whole-sheet
  // drag listener would fight both. Drag starts from the handle only.
  const dragControls = useDragControls();

  const needsRemarks = outcome !== 'Accepted';
  // A refusal is not something anyone should be asked to sign for.
  const showSignature = outcome !== 'Rejected';

  const handleClose = () => {
    setOutcome('Accepted');
    setRemarks('');
    signatureRef.current?.clear();
    onClose();
  };

  const handleSubmit = async () => {
    if (needsRemarks && !remarks.trim()) {
      message.error(translate(t, TRANSLATIONS.HANDOVER_ACK_REMARKS_REQUIRED));
      return;
    }
    setSubmitting(true);
    try {
      const signature = showSignature ? await signatureRef.current?.toBlob() : null;
      const report = await acknowledgeCheckInOutReport(
        bookingId,
        reportType,
        outcome,
        needsRemarks ? remarks.trim() : undefined,
        signature ?? undefined
      );
      message.success(translate(t, TRANSLATIONS.HANDOVER_ACK_SUCCESS));
      onAcknowledged(report);
      handleClose();
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HANDOVER_ACK_ERROR));
    } finally {
      setSubmitting(false);
    }
  };

  const renderOutcomeTile = (choice: OutcomeChoice) => {
    const selected = outcome === choice.outcome;
    const isReject = choice.outcome === 'Rejected';
    const accent = isReject ? token.colorError : TEAL;
    const accentBg = isReject ? token.colorErrorBg : TEAL_SOFT_BG;
    const Icon = choice.icon;

    return (
      <button
        key={choice.outcome}
        type="button"
        onClick={() => setOutcome(choice.outcome)}
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'flex-start',
          textAlign: 'left',
          width: '100%',
          padding: 12,
          borderRadius: 16,
          cursor: 'pointer',
          background: selected ? accentBg : token.colorBgContainer,
          border: `1.5px solid ${selected ? accent : token.colorBorderSecondary}`,
          transition: 'background 160ms ease, border-color 160ms ease',
        }}
      >
        <span
          style={{
            display: 'flex',
            flexShrink: 0,
            width: 32,
            height: 32,
            borderRadius: '50%',
            alignItems: 'center',
            justifyContent: 'center',
            background: selected ? accent : token.colorFillQuaternary,
            color: selected ? '#fff' : token.colorTextTertiary,
          }}
        >
          <Icon size={18} />
        </span>
        <span style={{ minWidth: 0 }}>
          <Text strong style={{ display: 'block', fontSize: 15 }}>
            {translate(t, choice.labelKey)}
          </Text>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {translate(t, choice.detailKey)}
          </Text>
        </span>
      </button>
    );
  };

  const body = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {OUTCOMES.map(renderOutcomeTile)}

      {needsRemarks && (
        <TextArea
          rows={3}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder={translate(t, TRANSLATIONS.HANDOVER_ACK_REMARKS_PLACEHOLDER)}
          maxLength={1000}
          style={{ borderRadius: 16, marginTop: 4 }}
        />
      )}

      {showSignature && <SignaturePad ref={signatureRef} />}
    </div>
  );

  const confirmButton = (
    <Button
      block
      type="primary"
      size="large"
      loading={submitting}
      onClick={handleSubmit}
      style={{ borderRadius: 999 }}
    >
      {translate(t, TRANSLATIONS.HANDOVER_ACK_CONFIRM)}
    </Button>
  );

  if (isMobile) {
    return createPortal(
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="handover-ack-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleClose}
              style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.45)' }}
            />
            <motion.div
              key="handover-ack-sheet"
              drag="y"
              dragControls={dragControls}
              dragListener={false}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.5 }}
              dragSnapToOrigin
              onDragEnd={(_, info) => {
                if (info.offset.y > 80 || info.velocity.y > 500) handleClose();
              }}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              style={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2101,
                width: '100%',
                maxWidth: 480,
                margin: '0 auto',
                background: token.colorBgContainer,
                borderRadius: '20px 20px 0 0',
                boxShadow: '0 -8px 24px rgba(0,0,0,0.16)',
                padding: '10px 20px calc(20px + env(safe-area-inset-bottom, 0px))',
                // The signature pad plus three tiles can outgrow a short
                // phone in landscape -- scroll the sheet rather than clip
                // the confirm button off the bottom of it.
                maxHeight: '88vh',
                overflowY: 'auto',
              }}
            >
              <div
                onPointerDown={(e) => dragControls.start(e)}
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  background: token.colorBorder,
                  margin: '0 auto 16px',
                  cursor: 'grab',
                  touchAction: 'none',
                }}
              />
              <Text strong style={{ display: 'block', fontSize: 16 }}>
                {translate(t, TRANSLATIONS.HANDOVER_ACK_TITLE)}
              </Text>
              <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 14 }}>
                {translate(t, TRANSLATIONS.HANDOVER_ACK_SUBTITLE)}
              </Text>
              {body}
              <div style={{ marginTop: 16 }}>{confirmButton}</div>
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    );
  }

  return (
    <Modal
      title={translate(t, TRANSLATIONS.HANDOVER_ACK_TITLE)}
      open={open}
      onCancel={handleClose}
      footer={confirmButton}
      destroyOnClose
    >
      <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 14 }}>
        {translate(t, TRANSLATIONS.HANDOVER_ACK_SUBTITLE)}
      </Text>
      {body}
    </Modal>
  );
};

export default HandoverAcknowledgementSheet;
